import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable, from, split } from '@apollo/client';
import { appName } from '@utils/localStorageHelpers';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { SentryLink } from 'apollo-link-sentry';
import { catchError, getPropagationContext } from '@utils/sentry';
import { setContext } from '@apollo/client/link/context';
import { getFirebaseIdToken } from '@utils/firebase';
import { getRuntimeConfigSync } from '@utils/runtimeConfig';
import store from '../store';
export const HASURA_INTERNAL_ERROR_MESSAGE = 'internal error';
/**
 * Apollo Client Singleton Class with Lazy Initialization
 * The client is created only when first accessed, using runtime configuration
 */
class ApolloClientManager {
    static instance;
    _client = null;
    _wsClient = null;
    _isFallbackActive = false;
    constructor() {
        if (!ApolloClientManager.instance) {
            ApolloClientManager.instance = this;
        }
        return ApolloClientManager.instance;
    }
    /**
     * Get the Apollo client instance (lazy initialization)
     * Creates the client on first access using runtime configuration
     */
    get client() {
        if (!this._client) {
            this._client = this.createClient();
        }
        return this._client;
    }
    /**
     * Create retry link for failed requests
     */
    getRetryLink() {
        return new RetryLink({
            delay: {
                initial: 2000,
                max: Infinity,
                jitter: true,
            },
            attempts: {
                max: 2,
                retryIf: (error) => !!error && process.env.NODE_ENV !== 'test',
            },
        });
    }
    /**
     * Create HTTP link with fallback logic
     * Uses runtime configuration for URLs
     */
    getHttpLinkWithFallback() {
        const runtimeConfig = getRuntimeConfigSync();
        const primaryHttpLink = new HttpLink({ uri: `${runtimeConfig.HASURA_HTTP}/v1/graphql` });
        const fallbackHttpLink = new HttpLink({
            uri: `${runtimeConfig.SECONDARY_HASURA_HTTP}/v1/graphql` || 'https://graphql.talview.com/v1/graphql',
        });
        const fallbackFlagLink = new ApolloLink((operation, forward) => {
            operation.setContext({
                ...operation.getContext(),
                useGraphQLFallback: this._isFallbackActive,
            });
            return forward(operation);
        });
        const fallbackErrorLink = onError(({ networkError, operation, forward }) => {
            if (networkError && !operation.getContext().useGraphQLFallback) {
                console.warn('Primary GraphQL endpoint failed. Switching to fallback endpoint for this session...');
                this._isFallbackActive = true;
                operation.setContext({
                    ...operation.getContext(),
                    useGraphQLFallback: true,
                });
                return forward(operation);
            }
            return;
        });
        const routingLink = ApolloLink.split((operation) => operation.getContext().useGraphQLFallback === true, fallbackHttpLink, primaryHttpLink);
        const retryLink = this.getRetryLink();
        return ApolloLink.from([fallbackFlagLink, fallbackErrorLink, retryLink, routingLink]);
    }
    /**
     * Create WebSocket link for subscriptions
     * Uses runtime configuration for URL
     */
    getWsLink() {
        const runtimeConfig = getRuntimeConfigSync();
        this._wsClient = createClient({
            url: `${runtimeConfig.HASURA_WS}/v1/graphql`,
            connectionParams: async () => {
                const idToken = await getFirebaseIdToken();
                const headers = {
                    Authorization: `Bearer ${idToken}`,
                    'x-hasura-role': 'CANDIDATE',
                };
                return { headers };
            },
        });
        return new GraphQLWsLink(this._wsClient);
    }
    /**
     * Create split link that routes queries/mutations to HTTP and subscriptions to WebSocket
     */
    getSplitLink() {
        const httpLinkWithFallback = this.getHttpLinkWithFallback();
        const wsLink = this.getWsLink();
        return split(({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        }, wsLink, httpLinkWithFallback);
    }
    /**
     * Create link to collect Firebase ID token
     */
    getCollectTokenLink() {
        return setContext(async () => {
            const firebaseToken = await getFirebaseIdToken();
            const state = store.getState();
            const idToken = firebaseToken || state?.auth?.accessToken || null;
            return { idToken };
        });
    }
    /**
     * Create authentication link that adds token to headers
     */
    getAuthLink() {
        return new ApolloLink((operation, forward) => {
            const context = operation.getContext();
            const idToken = context?.idToken;
            if (idToken && !context.skipAuthorization) {
                operation.setContext(({ headers = {} }) => ({
                    headers: {
                        ...headers,
                        Authorization: `Bearer ${idToken}`,
                        'x-hasura-role': 'CANDIDATE',
                    },
                }));
            }
            return forward(operation);
        });
    }
    /**
     * Create Sentry tracing link
     */
    getTracingLink() {
        const STATUS_OK = { code: 1, message: 'ok' };
        const STATUS_ERROR = { code: 2, message: 'internal_error' };
        return new ApolloLink((operation, forward) => {
            if (!forward) {
                return null;
            }
            const operationType = getOperationType(operation.query);
            const { span, traceHeader } = getPropagationContext(operation.operationName, operationType);
            if (traceHeader) {
                operation.setContext(({ headers = {} }) => ({
                    headers: {
                        ...headers,
                        'Sentry-Trace': traceHeader,
                    },
                }));
            }
            let spanClosed = false;
            const finishSpan = (status) => {
                if (!span || spanClosed) {
                    return;
                }
                if (typeof span.setStatus === 'function') {
                    span.setStatus(status);
                }
                span.end();
                spanClosed = true;
            };
            return new Observable((observer) => {
                const subscription = forward(operation).subscribe({
                    next: (result) => {
                        observer.next(result);
                    },
                    error: (error) => {
                        if (span) {
                            span.setAttribute('graphql.error', error?.message ?? 'Unknown error');
                        }
                        Sentry.captureException(error);
                        finishSpan(STATUS_ERROR);
                        observer.error(error);
                    },
                    complete: () => {
                        finishSpan(STATUS_OK);
                        observer.complete();
                    },
                });
                return () => {
                    finishSpan(STATUS_OK);
                    subscription.unsubscribe();
                };
            });
        });
    }
    /**
     * Create error handling link
     */
    getErrorLink() {
        return onError(({ operation, graphQLErrors, networkError, forward }) => {
            if (graphQLErrors) {
                const hasInternalError = graphQLErrors.some(({ message }) => message === HASURA_INTERNAL_ERROR_MESSAGE);
                graphQLErrors.forEach(({ message, extensions }) => catchError({
                    title: 'GraphQL error',
                    error: Error(`Message: ${message}, extensions: ${JSON.stringify(extensions)}, operationName: ${operation.operationName}, variables: ${JSON.stringify(operation.variables)}`),
                    skipToast: true,
                }));
                // Retry once on Hasura internal errors (race condition on cold start).
                // Skip mutations — retrying a mutation that already committed risks duplicate writes.
                const definition = getMainDefinition(operation.query);
                const isMutation = definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
                if (hasInternalError && !isMutation) {
                    return forward(operation);
                }
            }
            if (networkError)
                catchError({ title: 'Network error', error: networkError, skipToast: true });
        });
    }
    /**
     * Create link that enriches Sentry breadcrumbs/fingerprints for GraphQL ops
     */
    getSentryBreadcrumbLink() {
        return new SentryLink({
            attachBreadcrumbs: {},
        });
    }
    /**
     * Create Apollo Client with runtime configuration
     * This is called lazily when client is first accessed
     */
    createClient() {
        console.log('[Apollo] Creating Apollo Client with runtime configuration...');
        const splitLink = this.getSplitLink();
        const collectTokenLink = this.getCollectTokenLink();
        const authLink = this.getAuthLink();
        const sentryBreadcrumbLink = this.getSentryBreadcrumbLink();
        const tracingLink = this.getTracingLink();
        const errorLink = this.getErrorLink();
        const cache = new InMemoryCache();
        const client = new ApolloClient({
            link: from([errorLink, sentryBreadcrumbLink, tracingLink, collectTokenLink, authLink, splitLink]),
            cache,
            name: appName,
            version: process.env.VERSION || 'local',
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'cache-and-network',
                },
            },
        });
        console.log('[Apollo] Apollo Client created successfully');
        return client;
    }
    /**
     * Remove/cleanup Apollo client
     */
    async removeClient() {
        if (this._client) {
            await this._client.resetStore();
            await this._client.clearStore();
            this._client.stop();
            this._client = null;
        }
        if (this._wsClient) {
            this._wsClient.dispose();
            this._wsClient = null;
        }
        this._isFallbackActive = false;
    }
}
const instance = new ApolloClientManager();
/**
 * Proxy that forwards method calls to the actual Apollo client
 * This allows utilities to use the client directly: client.query(), client.mutate(), etc.
 * The client is created lazily on first access
 */
const clientProxy = new Proxy(instance, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Proxy get trap forwards an arbitrary string-keyed property from either ApolloClientManager or the underlying ApolloClient, so its return type can't be known statically
    get(target, prop) {
        // First check if the property exists on the ApolloClientManager instance itself
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ApolloClientManager has no index signature, so a cast is needed to read target[prop] with an arbitrary prop key
        if (prop in target && typeof target[prop] !== 'undefined') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- same dynamic property lookup as above, needed to return target[prop] for an arbitrary prop key
            return target[prop];
        }
        // Otherwise, forward to the actual Apollo client (which will be created lazily)
        const actualClient = target.client;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ApolloClient<unknown> has no index signature, so a cast is needed to forward an arbitrary prop key to the actual client
        const value = actualClient[prop];
        // Bind methods to the actual client to preserve 'this' context
        if (typeof value === 'function') {
            return value.bind(actualClient);
        }
        return value;
    },
});
/**
 * Remove/cleanup Apollo client (re-export for backward compatibility)
 */
export const removeClient = async () => {
    await instance.removeClient();
};
export default clientProxy;
const getOperationType = (query) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' ? definition.operation : 'query';
};
